import {useState} from 'react'
import {collection, deleteDoc, doc, getDocs, query, updateDoc, where} from 'firebase/firestore'
import {getDownloadURL, getStorage, uploadBytes, deleteObject} from 'firebase/storage'
import {ref as storageRef} from 'firebase/storage'
import JSZip from 'jszip'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {DataroomFile, Audit, AuditDocument} from '../types/audit.types'

export const useAuditActions = (
  dataroom: DataroomFile[],
  setSelectedFileName: React.Dispatch<React.SetStateAction<{[key: string]: string}>>
) => {
  const {currentUser} = useAuth()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [selectedAuditId, setSelectedAuditID] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, auditId: string) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      const storage = getStorage()
      const tenantId = currentUser?.tenantId || ''
      const filePath = `/tenants/${tenantId}/audits/${auditId}/${file.name}`
      const fileRef = storageRef(storage, filePath)

      try {
        const uploadResult = await uploadBytes(fileRef, file)
        const downloadURL = await getDownloadURL(uploadResult.ref)
        const tenantRef = doc(db, 'tenants', tenantId)
        const auditRef = doc(tenantRef, 'audits', auditId)
        await updateDoc(auditRef, {
          audit_documents: [
            {
              fileURL: downloadURL,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              uploadedBy: currentUser?.userName,
              uploadedUid: currentUser?.uid,
            },
          ],
        })
        setSelectedFileName((prev) => ({
          ...prev,
          [auditId]: file.name,
        }))
        showSuccessToast({message: 'Audit file uploaded successfully'})
      } catch (error) {
        console.error('Error uploading file and updating Firestore:', error)
      }
    }
  }

  const triggerFileInputClick = (auditId: string) => {
    document.getElementById(`hiddenFileInput-${auditId}`)?.click()
  }

  const handleDownloadReport = async (
    auditID: string,
    dataroomFolderID: string,
    auditEvidenceFolderName: string
  ) => {
    setDownloadLoading(true)
    setSelectedAuditID(auditID)
    try {
      if (dataroomFolderID) {
        const filterData = dataroom.filter((data) => data.parentFolder === dataroomFolderID)

        if (filterData.length > 0) {
          const zip = new JSZip()

          for (const fileData of filterData) {
            const {downloadUrl, name} = fileData

            const response = await fetch(downloadUrl)

            if (!response.ok) {
              continue
            }

            const blob = await response.blob()
            zip.file(name || 'file', blob)
          }

          const zipBlob = await zip.generateAsync({type: 'blob'})
          const blobUrl = URL.createObjectURL(zipBlob)

          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `${auditEvidenceFolderName}.zip`
          link.click()

          URL.revokeObjectURL(blobUrl)
          setDownloadLoading(false)
        } else {
          setDownloadLoading(false)
        }
      }
    } catch (error) {
      setDownloadLoading(false)
    }
  }

  const handleDownloadAuditReport = async (auditID: string, auditDocument: AuditDocument) => {
    try {
      if (
        auditDocument &&
        'fileURL' in auditDocument &&
        'fileName' in auditDocument &&
        'fileType' in auditDocument
      ) {
        const response = await fetch(auditDocument.fileURL)
        const fileContent = await response.blob()

        const blob = new Blob([fileContent], {type: auditDocument.fileType})

        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = auditDocument.fileName

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
        showSuccessToast({message: 'Audit report downloaded!'})
      } else {
        showErrorToast({message: 'Audit report missing'})
      }
    } catch (error) {
      showErrorToast({message: 'Error downloading file'})
    }
  }

  const removeAuditDocument = async (auditId: string, documentToRemove: AuditDocument) => {
    const tenantId = currentUser?.tenantId
    if (!tenantId || !documentToRemove?.fileName) {
      showErrorToast({message: 'File information is missing.'})
      return
    }

    const storage = getStorage()
    const filePath = `/tenants/${tenantId}/audits/${auditId}/${documentToRemove.fileName}`
    const fileRef = storageRef(storage, filePath)

    try {
      await deleteObject(fileRef)

      const tenantRef = doc(db, 'tenants', tenantId)
      const auditRef = doc(tenantRef, 'audits', auditId)
      await updateDoc(auditRef, {
        audit_documents: [],
      })

      setSelectedFileName((prev) => {
        const newState = {...prev}
        delete newState[auditId]
        return newState
      })
      showSuccessToast({message: 'File removed successfully'})
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
        try {
          const tenantRef = doc(db, 'tenants', tenantId)
          const auditRef = doc(tenantRef, 'audits', auditId)
          await updateDoc(auditRef, {
            audit_documents: [],
          })
          setSelectedFileName((prev) => {
            const newState = {...prev}
            delete newState[auditId]
            return newState
          })
          showSuccessToast({message: 'File removed successfully'})
          console.warn('File not found in storage, but removed from Firestore.')
        } catch (dbError) {
          console.error('Error removing file from Firestore:', dbError)
          showErrorToast({message: 'Error removing file'})
        }
      } else {
        console.error('Error removing file:', error)
        showErrorToast({message: 'Error removing file'})
      }
    }
  }

  const removeAudit = async (toDeleteAudit: Audit) => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      if (toDeleteAudit.dataroomFolderID) {
        const relatedFiles = dataroom.filter(
          (file) => file.parentFolder === toDeleteAudit.dataroomFolderID
        )
        const storage = getStorage()
        for (const file of relatedFiles) {

          try {
            const filePath = `tenants/${tenantId}/dataroom/${toDeleteAudit.auditEvidenceFolderName || ''}/${file.name}`
            const fileRef = storageRef(storage, filePath)
            await deleteObject(fileRef)
          } catch (err: any) {
            if (err.code !== 'storage/object-not-found') {
              console.error('Error deleting file from storage:', err)
            }
          }
          try {
            const fileDocRef = doc(db, 'tenants', tenantId, 'dataroom', file.id)
            await deleteDoc(fileDocRef)
          } catch (err: any) {
            console.error('Error deleting dataroom Firestore doc:', err)
          }
        }
      }

      const dataroomRef = collection(tenantRef, 'dataroom')
      const dataRef = doc(dataroomRef, toDeleteAudit.dataroomFolderID)
      await deleteDoc(dataRef)

      const policyRef = collection(tenantRef, 'audits')
      const docRef = doc(policyRef, toDeleteAudit.id)
      await deleteDoc(docRef)

      if (toDeleteAudit.auditor) {
        const employeesRef = collection(tenantRef, 'employees')
        const querySnapshot = await getDocs(
          query(employeesRef, where('uid', '==', toDeleteAudit.auditor?.id))
        )
        if (!querySnapshot.empty) {
          const employeeDocRef = querySnapshot.docs[0].ref
          await updateDoc(employeeDocRef, {availability: true})
        }
      }
      showSuccessToast({
        message: `${toDeleteAudit.framework[0]} audit has been deleted successfully!`,
      })
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the audit evidence.'})
    } finally {
      setIsDeleting(false)
    }
  }

  const handleView = (auditDocument: AuditDocument) => {
    if (auditDocument.fileURL) {
      const { fileName,fileType,fileURL } = auditDocument
      let openInNewTab = false;
      let url = fileURL;

      switch (fileType) {

        case 'image/png':
        case 'image/jpeg':
        case 'image/jpg':
        case 'image/gif':
        case 'image/webp':
        case 'image/svg+xml':

        case 'application/pdf':
          openInNewTab = true;
          break;

        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          url = `https://docs.google.com/gview?url=${encodeURIComponent(fileURL)}&embedded=true`;
          openInNewTab = true;
          break;

        default:
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
      }

      if (openInNewTab) {
        window.open(url, '_blank');
      }
    } else {
      console.error('No download URL available for this item.');
    }
  };
  const handleOpenAudit = (audit: Audit) => {
    // Store audit context in localStorage
    localStorage.setItem('currentAuditContext', JSON.stringify({
      auditId: audit.id,
      framework: audit.framework,
      auditTitle: audit.auditTitle,
      auditorId: audit.auditor?.id,
      auditView: true,
      auditStatus: audit.status,
      auditStartDate: audit.start_date,
      auditEndDate: audit.end_date,
      auditAuditor: audit.auditor?.email,
      auditOrganization: audit.organization,
    }))
    window.location.reload()
    window.dispatchEvent(new Event('auditContextChanged'))
  }

  return {
    downloadLoading,
    selectedAuditId,
    isDeleting,
    handleFileChange,
    triggerFileInputClick,
    handleDownloadReport,
    handleDownloadAuditReport,
    removeAudit,
    removeAuditDocument,
    handleView,
    handleOpenAudit,
  }
}
